import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  createProduction,
  updateProduction,
} from "../../../../redux/actions/production";
import { getMaterials } from "../../../../redux/actions/material";
import { store } from "../../../../store";
import {
  getMaterialProtocols,
  getProtocols,
} from "../../../../services/protocolService";
import {
  closeLoadingAlert,
  showErrorAlert,
  showLoadingAlert,
} from "../../../ui/utils/AlertUtils";
import Modal from "../../../../shared/ui/modal/Modal";
import { showSuccessToast } from "../../../../shared/ui/toast/Toast";

dayjs.locale("en-gb");

const CDL_LOCATION = "cdl";
const sectionSx = {
  bgcolor: "grey.50",
  borderRadius: 2,
  p: 2,
};

const asDate = (value) => (value ? dayjs(value) : null);
const asApiDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : null);

const emptyForm = {
  materialId: "",
  batchNumber: "",
  quantity: "",
  reference: "",
  productionDate: null,
  expirationDate: null,
  sendingAuthorization: null,
  comment: "",
  cdlComment: "",
  protocolIds: [],
  location: CDL_LOCATION,
  patientNumber: "",
  attributionDate: null,
  destructionDate: null,
};

const getInitialForm = (production) => {
  if (!production) {
    return { ...emptyForm };
  }

  return {
    materialId: production.materialId || production.Material?.id || "",
    batchNumber: production.batchNumber || "",
    quantity: "1",
    reference: production.reference || "",
    productionDate: asDate(production.productionDate),
    expirationDate: asDate(production.expirationDate),
    sendingAuthorization: asDate(production.sendingAuthorization),
    comment: production.comment || "",
    cdlComment: production.cdlComment || "",
    protocolIds: production.Protocols?.map((protocol) => protocol.id) || [],
    location:
      production.centerId == null
        ? CDL_LOCATION
        : String(production.centerId),
    patientNumber: production.patientNumber || "",
    attributionDate: asDate(production.attributionDate),
    destructionDate: asDate(production.destructionDate),
  };
};

const ProductionDatePicker = ({
  label,
  value,
  onChange,
  action,
  error = false,
  required = false,
  ...props
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
    <Box sx={{ position: "relative", width: "100%" }}>
      <DatePicker
        label={label}
        value={value}
        onChange={onChange}
        format="DD/MM/YYYY"
        sx={{ width: "100%" }}
        slotProps={{
          textField: {
            fullWidth: true,
            error,
            required,
            sx: action
              ? {
                  "& .MuiInputBase-input": {
                    pr: 10,
                  },
                }
              : undefined,
          },
        }}
        {...props}
      />
      {action && (
        <Button
          type="button"
          size="small"
          aria-label={action.ariaLabel}
          onMouseDown={(event) => event.preventDefault()}
          onClick={action.onClick}
          sx={{
            position: "absolute",
            right: 42,
            top: "50%",
            minWidth: 38,
            px: 0.5,
            transform: "translateY(-50%)",
            color: action.active ? "primary.main" : "text.secondary",
            bgcolor: action.active ? "action.selected" : "transparent",
            zIndex: 1,
          }}>
          {action.label}
        </Button>
      )}
    </Box>
  </LocalizationProvider>
);

export const ProductionForm = ({
  open,
  onClose,
  onSaved,
  onDelete,
  production = null,
  productions = [],
  centers = [],
  studyCode,
  studyId,
  canDelete = false,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(production);
  const isLoading = useSelector(
    (state) => state.production.isLoadingProduction,
  );
  const materials = useSelector((state) => state.material.materials);
  const [form, setForm] = React.useState(() => getInitialForm(production));
  const [studyProtocols, setStudyProtocols] = React.useState([]);
  const [materialProtocols, setMaterialProtocols] = React.useState([]);
  const [isKitDemo, setIsKitDemo] = React.useState(false);
  const [isExpirationDateNA, setIsExpirationDateNA] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [attributionDatePickerKey, setAttributionDatePickerKey] = React.useState(0);

  const selectedMaterial = React.useMemo(
    () =>
      materials.find((material) => material.id === form.materialId) ||
      production?.Material ||
      null,
    [form.materialId, materials, production],
  );
  const sortedCenters = React.useMemo(
    () =>
      [...centers].sort((left, right) =>
        String(left.centerNumber || "").localeCompare(
          String(right.centerNumber || ""),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [centers],
  );
  const referenceNA = `${studyCode?.replace("-", "").slice(2) || ""}NA`;
  const referenceNAAvailable =
    Boolean(selectedMaterial) && selectedMaterial.type !== "material";
  const expirationNAAvailable = referenceNAAvailable;
  const kitDemoAvailable = selectedMaterial?.type === "material";

  const references = React.useMemo(() => {
    if (isEdit) {
      return form.reference ? [form.reference] : [];
    }

    const count = Number.parseInt(form.quantity, 10);
    if (!form.reference || !Number.isInteger(count) || count < 1) {
      return [];
    }

    if (form.reference === referenceNA) {
      return Array.from({ length: count }, () => referenceNA);
    }

    const match = form.reference.match(/^(.*?)(\d+)$/);
    if (!match) {
      return [];
    }

    const start = Number.parseInt(match[2], 10);
    return Array.from({ length: count }, (_, index) => {
      const suffix = String(start + index).padStart(match[2].length, "0");
      return `${match[1]}${suffix}`;
    });
  }, [form.quantity, form.reference, isEdit, referenceNA]);

  const duplicateReference = React.useMemo(() => {
    const existingReferences = new Set(
      productions
        .filter((item) => item.id !== production?.id)
        .map((item) => item.reference),
    );
    return references.find(
      (reference) =>
        !reference.includes("NA") && existingReferences.has(reference),
    );
  }, [production?.id, productions, references]);

  const referencePreview = React.useMemo(() => {
    if (references.length === 0) {
      return "";
    }
    if (references.length === 1) {
      return "";
    }
    return `... ${references[references.length - 1]}`;
  }, [references]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setForm(getInitialForm(production));
    const hasLongExpiration = Boolean(
      production?.expirationDate &&
        dayjs(production.expirationDate).isSame(dayjs("2999-01-01"), "day"),
    );
    setIsKitDemo(
      hasLongExpiration && production?.Material?.type === "material",
    );
    setIsExpirationDateNA(
      hasLongExpiration && production?.Material?.type !== "material",
    );
    setFieldErrors({});
  }, [open, production]);

  React.useEffect(() => {
    if (open && studyId) {
      store.dispatch(getMaterials(studyId));
    }
  }, [open, studyId]);

  React.useEffect(() => {
    if (!open || !studyId) {
      setStudyProtocols([]);
      return;
    }

    getProtocols(studyId)
      .then((list) => setStudyProtocols(Array.isArray(list) ? list : []))
      .catch(() => setStudyProtocols([]));
  }, [open, studyId]);

  React.useEffect(() => {
    if (!open || !form.materialId) {
      setMaterialProtocols([]);
      return;
    }

    getMaterialProtocols(form.materialId)
      .then((list) => setMaterialProtocols(Array.isArray(list) ? list : []))
      .catch(() => setMaterialProtocols([]));
  }, [form.materialId, open]);

  const clearFieldErrors = (...fields) => {
    setFieldErrors((current) => {
      const next = { ...current };
      fields.forEach((field) => delete next[field]);
      return next;
    });
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    clearFieldErrors(field);
  };

  const handleMaterialChange = (materialId) => {
    const resetLongExpiration = isKitDemo || isExpirationDateNA;
    setForm((current) => ({
      ...current,
      materialId,
      protocolIds: [],
      expirationDate: resetLongExpiration ? null : current.expirationDate,
    }));
    setIsKitDemo(false);
    setIsExpirationDateNA(false);
    clearFieldErrors(
      "materialId",
      "protocolIds",
      "batchNumber",
      "expirationDate",
    );
  };

  const handleProtocolsChange = (event) => {
    const nextProtocolIds = event.target.value;
    const newlySelected = nextProtocolIds.filter(
      (id) => !form.protocolIds.includes(id),
    );
    const outsideMaterial = studyProtocols.find(
      (protocol) =>
        newlySelected.includes(protocol.id) &&
        !materialProtocols.some((item) => item.id === protocol.id),
    );

    if (
      outsideMaterial &&
      !window.confirm(
        t("protocolNotCorrespondWithMaterial", {
          protocol: outsideMaterial.reference,
          material: selectedMaterial?.name || "",
          defaultValue:
            "This protocol does not correspond with material {{material}} ({{protocol}}). Continue?",
        }),
      )
    ) {
      return;
    }

    updateField("protocolIds", nextProtocolIds);
  };

  const handleAttributionDateChange = (value) => {
    if (
      value &&
      form.expirationDate &&
      dayjs(value).isAfter(dayjs(form.expirationDate), "day") &&
      !window.confirm(
        t("attributionDateAfterExpirationConfirmation", {
          defaultValue:
            "The attribution date is later than the expiration date. Continue?",
        }),
      )
    ) {
      // Reset the picker's internal draft to the previously accepted date.
      setAttributionDatePickerKey((current) => current + 1);
      return;
    }

    updateField("attributionDate", value);
    clearFieldErrors("patientNumber");
  };

  const validate = () => {
    const errors = {};
    const materialRequiresDates = selectedMaterial?.type === "material";

    if (!selectedMaterial) errors.materialId = true;
    if (!form.reference) errors.reference = true;
    if (!form.productionDate) errors.productionDate = true;
    if (!form.sendingAuthorization) errors.sendingAuthorization = true;

    if (!isEdit) {
      const quantity = Number(form.quantity);
      if (!Number.isInteger(quantity) || quantity < 1) errors.quantity = true;
      if (form.reference && references.length === 0) errors.reference = true;
      if (form.protocolIds.length === 0) errors.protocolIds = true;
    }

    if (materialRequiresDates) {
      if (!form.batchNumber) errors.batchNumber = true;
      if (!form.expirationDate) errors.expirationDate = true;
    }

    if (duplicateReference) {
      errors.reference = true;
    }

    if (
      form.productionDate &&
      form.expirationDate &&
      !dayjs(form.expirationDate).isAfter(dayjs(form.productionDate), "day")
    ) {
      errors.expirationDate = true;
    }

    if (
      dayjs(form.sendingAuthorization).isBefore(dayjs(form.productionDate), "day") ||
      (form.expirationDate &&
        !dayjs(form.sendingAuthorization).isBefore(
          dayjs(form.expirationDate),
          "day",
        ))
    ) {
      errors.sendingAuthorization = true;
    }

    if (
      (form.patientNumber && !form.attributionDate) ||
      (!form.patientNumber && form.attributionDate)
    ) {
      errors.patientNumber = true;
      errors.attributionDate = true;
    }

    return errors;
  };

  const getUpdateStatus = () => {
    if (form.destructionDate) {
      return "destroyed";
    }
    if (form.patientNumber && form.attributionDate) {
      return "attributed to patient";
    }

    const originalLocation =
      production.centerId == null
        ? CDL_LOCATION
        : String(production.centerId);
    const clearingTerminalStatus = [
      "destroyed",
      "attributed to patient",
    ].includes(production.status);
    if (form.location !== originalLocation || clearingTerminalStatus) {
      return form.location === CDL_LOCATION ? "produced" : "at center";
    }

    return production.status;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    showLoadingAlert(
      isEdit
        ? t("productionUpdating", { defaultValue: "Updating production..." })
        : t("productionCreating", { defaultValue: "Creating productions..." }),
    );

    try {
      const commonBody = {
        batchNumber: form.batchNumber || null,
        productionDate: asApiDate(form.productionDate),
        expirationDate: asApiDate(form.expirationDate),
        sendingAuthorization: asApiDate(form.sendingAuthorization),
        comment: form.comment,
        cdlComment: form.cdlComment,
        materialId: form.materialId,
      };

      if (isEdit) {
        await store.dispatch(
          updateProduction(production.id, {
            ...commonBody,
            reference: form.reference,
            protocols: form.protocolIds,
            centerId:
              form.location === CDL_LOCATION ? null : Number(form.location),
            patientNumber: form.patientNumber || null,
            attributionDate: form.patientNumber
              ? asApiDate(form.attributionDate)
              : null,
            destructionDate: asApiDate(form.destructionDate),
            status: getUpdateStatus(),
          }),
        );
      } else {
        for (const reference of references) {
          await store.dispatch(
            createProduction({
              ...commonBody,
              reference,
              protocolIds: form.protocolIds,
              status: "produced",
            }),
          );
        }
      }

      closeLoadingAlert();
      showSuccessToast(
        isEdit
          ? t("productionUpdatedSuccess", {
              defaultValue: "Production updated successfully",
            })
          : t("productionsCreatedSuccess"),
      );
      onSaved?.();
      onClose();
    } catch (error) {
      closeLoadingAlert();
      console.error("Failed to save production:", error);
      showErrorAlert(
        isEdit
          ? t("productionUpdateError", {
              defaultValue: "An error occurred while updating the production",
            })
          : t("productionCreationError"),
      );
    }
  };

  const toggleLongExpiration = (checked, field) => {
    if (field === "kit") {
      setIsKitDemo(checked);
      setIsExpirationDateNA(false);
    } else {
      setIsExpirationDateNA(checked);
      setIsKitDemo(false);
    }
    updateField("expirationDate", checked ? dayjs("2999-01-01") : null);
  };

  const handleExpirationDateChange = (value) => {
    setIsKitDemo(false);
    setIsExpirationDateNA(false);
    updateField("expirationDate", value);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeDisabled={isLoading}
      title={isEdit ? t("editProduction") : t("createProduction")}
      maxWidth="md"
      contentSx={{ p: 0, overflow: "hidden" }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 112px)",
        }}>
        <Box
          sx={{ px: 3, pt: 1, pb: 3, overflowY: "auto", minHeight: 0 }}>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={sectionSx}>
                <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={fieldErrors.materialId}>
              <InputLabel id="production-material-label">
                {t("material")}
              </InputLabel>
              <Select
                labelId="production-material-label"
                label={t("material")}
                value={form.materialId}
                disabled={isEdit}
                onChange={(event) =>
                  handleMaterialChange(event.target.value)
                }>
                {materials.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    {material.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={isEdit ? 6 : 4}>
            <TextField
              fullWidth
              required={selectedMaterial?.type === "material"}
              error={fieldErrors.batchNumber}
              label={t("batchNumber")}
              value={form.batchNumber}
              onChange={(event) =>
                updateField("batchNumber", event.target.value)
              }
            />
            {!isEdit &&
              selectedMaterial &&
              !["material", "document"].includes(selectedMaterial.type) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.batchNumber === referenceNA}
                      onChange={(event) =>
                        updateField(
                          "batchNumber",
                          event.target.checked ? referenceNA : "",
                        )
                      }
                    />
                  }
                  label={t("batchNa")}
                />
              )}
          </Grid>

          {!isEdit && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                error={fieldErrors.quantity}
                type="number"
                inputProps={{ min: 1 }}
                label={t("quantity")}
                value={form.quantity}
                onChange={(event) =>
                  updateField("quantity", event.target.value)
                }
              />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm={isEdit ? 6 : 4}>
            <Box sx={{ position: "relative" }}>
              {!isEdit &&
                referencePreview &&
                form.reference !== referenceNA && (
                  <Box
                    aria-label={t("references")}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: 8,
                      px: 0.5,
                      color: "text.secondary",
                      bgcolor: "background.paper",
                      fontSize: "0.7rem",
                      lineHeight: "16px",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}>
                    {referencePreview}
                  </Box>
                )}
              <TextField
                fullWidth
                required
                error={fieldErrors.reference}
                label={isEdit ? t("reference") : t("firstReference")}
                value={form.reference}
                onChange={(event) =>
                  updateField("reference", event.target.value)
                }
                InputProps={
                  referenceNAAvailable
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              type="button"
                              size="small"
                              aria-label={t("setReferenceNotApplicable", {
                                defaultValue: "Set reference to N/A",
                              })}
                              onClick={() =>
                                updateField("reference", referenceNA)
                              }
                              sx={{
                                minWidth: 38,
                                px: 0.5,
                                color:
                                  form.reference === referenceNA
                                    ? "primary.main"
                                    : "text.secondary",
                                bgcolor:
                                  form.reference === referenceNA
                                    ? "action.selected"
                                    : "transparent",
                              }}>
                              {t("notApplicable")}
                            </Button>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <ProductionDatePicker
              label={t("productionDate")}
              value={form.productionDate}
              required
              error={fieldErrors.productionDate}
              maxDate={dayjs()}
              onChange={(value) => updateField("productionDate", value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ProductionDatePicker
              label={t("expirationDate")}
              value={form.expirationDate}
              required={selectedMaterial?.type === "material"}
              error={fieldErrors.expirationDate}
              minDate={form.productionDate || undefined}
              onChange={handleExpirationDateChange}
              action={
                expirationNAAvailable
                  ? {
                      label: t("notApplicable"),
                      ariaLabel: t("setExpirationNotApplicable", {
                        defaultValue: "Set expiration date to N/A",
                      }),
                      active: isExpirationDateNA,
                      onClick: () => toggleLongExpiration(true, "na"),
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ProductionDatePicker
              label={t("sendingAuthorization")}
              value={form.sendingAuthorization}
              required
              error={fieldErrors.sendingAuthorization}
              minDate={form.productionDate || undefined}
              maxDate={
                form.expirationDate
                  ? dayjs(form.expirationDate).subtract(1, "day")
                  : undefined
              }
              onChange={(value) =>
                updateField("sendingAuthorization", value)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FormControlLabel
                disabled={!kitDemoAvailable}
                sx={{
                  m: 0,
                  pl: 0.5,
                  pr: 1.5,
                  height: 56,
                  minWidth: 120,
                  flexShrink: 0,
                  justifyContent: "center",
                  border: "1px solid",
                  borderColor: kitDemoAvailable
                    ? "divider"
                    : "action.disabledBackground",
                  borderRadius: 2,
                  bgcolor: isKitDemo ? "action.selected" : "transparent",
                }}
                control={
                  <Checkbox
                    checked={kitDemoAvailable && isKitDemo}
                    onChange={(event) =>
                      toggleLongExpiration(event.target.checked, "kit")
                    }
                  />
                }
                label={t("kitDemo")}
              />
              <FormControl
                fullWidth
                required={!isEdit}
                error={!isEdit && fieldErrors.protocolIds}
                sx={{ minWidth: 0 }}>
                <InputLabel id="production-protocol-label">
                  {t("protocols")}
                </InputLabel>
                <Select
                  labelId="production-protocol-label"
                  multiple
                  value={form.protocolIds}
                  onChange={handleProtocolsChange}
                  input={<OutlinedInput label={t("protocols")} />}
                  renderValue={(selected) =>
                    studyProtocols
                      .filter((protocol) => selected.includes(protocol.id))
                      .map((protocol) => protocol.reference)
                      .join(", ")
                  }>
                  {studyProtocols.map((protocol) => {
                    const belongsToMaterial = materialProtocols.some(
                      (item) => item.id === protocol.id,
                    );
                    return (
                      <MenuItem
                        key={protocol.id}
                        value={protocol.id}
                        sx={
                          !belongsToMaterial ? { color: "text.disabled" } : {}
                        }>
                        <Checkbox
                          checked={form.protocolIds.includes(protocol.id)}
                        />
                        {protocol.reference}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
                </Grid>
              </Box>
            </Grid>

          {isEdit && (
            <Grid item xs={12}>
              <Box sx={sectionSx}>
                <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="production-location-label">
                    {t("selectCenter")}
                  </InputLabel>
                  <Select
                    labelId="production-location-label"
                    label={t("selectCenter")}
                    value={form.location}
                    onChange={(event) =>
                      updateField("location", event.target.value)
                    }>
                    <MenuItem value={CDL_LOCATION}>{t("atCDL")}</MenuItem>
                    {sortedCenters.map((center) => (
                      <MenuItem key={center.id} value={String(center.id)}>
                        {center.centerNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ProductionDatePicker
                  label={t("destructionDate")}
                  value={form.destructionDate}
                  maxDate={dayjs()}
                  onChange={(value) => updateField("destructionDate", value)}
                  action={
                    form.destructionDate
                      ? {
                          label: t("clear"),
                          ariaLabel: t("clearDestructionDate"),
                          onClick: () => updateField("destructionDate", null),
                        }
                      : undefined
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("patientNumber")}
                  error={fieldErrors.patientNumber}
                  value={form.patientNumber}
                  onChange={(event) => {
                    updateField("patientNumber", event.target.value);
                    clearFieldErrors("attributionDate");
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ProductionDatePicker
                  key={attributionDatePickerKey}
                  label={t("attributionDate")}
                  value={form.attributionDate}
                  error={fieldErrors.attributionDate}
                  maxDate={dayjs()}
                  onChange={handleAttributionDateChange}
                />
              </Grid>
                </Grid>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={sectionSx}>
              {selectedMaterial?.type === "document" && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("specifyDocument")}
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={t("siteComment")}
                    value={form.comment}
                    onChange={(event) =>
                      updateField("comment", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={t("cdlComment")}
                    value={form.cdlComment}
                    onChange={(event) =>
                      updateField("cdlComment", event.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

        </Grid>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", alignItems: "center", px: 3, py: 2 }}>
          {isEdit && canDelete && (
            <Button
              type="button"
              color="error"
              onClick={onDelete}
              disabled={isLoading}>
              {t("deleteProduction")}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isEdit ? t("update") : t("create")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
